import { NOC_STAGES } from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { Link, type LinkProps } from "@tanstack/react-router";
import type { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/api/messages";
import type { SectorNumber } from "../../lib/dashboard/sectors";
import { RightIcon } from "./icons";
import "./sector.css";

/**
 * The sector dashboards' building blocks (`.sd-*`, sector.css), in the
 * accreditation dashboard's card language (`.db-card`): a panel per module
 * of the sector — its icon, name, one line, a way into the module page — and
 * a grid of numbers. Every number is a count the module's own summary
 * endpoint returned; nothing here counts rows.
 */
export type Tone = "n" | "ok" | "bl" | "r" | "y";

export function Panel({
	icon: Icon,
	title,
	sub,
	to,
	search,
	children,
	accent,
}: {
	icon: ComponentType<{ className?: string }>;
	title: string;
	sub?: string;
	to?: LinkProps["to"];
	search?: LinkProps["search"];
	children: ReactNode;
	/** A small badge beside the title (a phase state). */
	accent?: ReactNode;
}) {
	const { t } = useTranslation("dashboard");
	return (
		<section className="db-card sd-panel">
			<header className="sd-panel__h">
				<span className="sd-panel__ic">
					<Icon />
				</span>
				<h2>
					<span>
						{title}
						{accent}
					</span>
					{sub && <small>{sub}</small>}
				</h2>
				{to && (
					<Link to={to} search={search} className="sd-panel__go">
						{t("sector.open")}
						<RightIcon />
					</Link>
				)}
			</header>
			{children}
		</section>
	);
}

/** A grid of numbers: as many per row as the panel fits (sector.css). */
export function StatGrid({ children }: { children: ReactNode }) {
	return <div className="sd-stats">{children}</div>;
}

/** One number: label above, value, a muted line. `n` undefined = loading (a skeleton). */
export function Stat({ label, n, hint, tone = "n", to, search }: { label: string; n: number | undefined; hint?: string; tone?: Tone; to?: LinkProps["to"]; search?: LinkProps["search"] }) {
	const { formatNumber: fmt } = useUiStrings();
	const body = (
		<>
			<span className="sd-stat__l">{label}</span>
			{n === undefined ? <span className="skeleton my-[0.25rem] block h-[1.5rem] w-[3.5rem]" /> : <b className="sd-stat__n">{fmt(n)}</b>}
			{hint && <small className="sd-stat__s">{hint}</small>}
		</>
	);
	return to ? (
		<Link to={to} search={search} className={`sd-stat sd-stat--${tone} sd-stat--link`}>
			{body}
		</Link>
	) : (
		<div className={`sd-stat sd-stat--${tone}`}>{body}</div>
	);
}

/** A filled bar, `value` of `max` (occupied of all beds); labelled for screen readers. */
export function Meter({ value, max, label }: { value: number; max: number; label: string }) {
	const p = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
	return (
		<div className="sd-meter" role="img" aria-label={label}>
			<span style={{ width: `${p}%` }} />
		</div>
	);
}

/** A panel's read failed: its message, in the panel (the other panels still show). */
export function PanelError({ error }: { error: unknown }) {
	return (
		<p role="alert" className="sd-err">
			{errorMessage(error)}
		</p>
	);
}

/** The dashboard's lead line: what this sector is for. */
export function SectorIntro({ children }: { children: ReactNode }) {
	return <p className="sd-intro">{children}</p>;
}

/** "Sector N · Early Planning Phase" — the panel's title, as Sector Management names it. */
export function useSectorTitle() {
	const { t } = useTranslation("nocStages");
	return (n: SectorNumber) => {
		const s = NOC_STAGES.find((x) => x.stage === n)!;
		return `${t("stageLabel", { n })} · ${t(`stageName.${s.key}`)}`;
	};
}

/** A labelled meter: "Approved · 312 of 480". Loading = a skeleton line. */
export function Progress({ label, value, max }: { label: string; value: number | undefined; max: number | undefined }) {
	const { t } = useTranslation("dashboard");
	const { formatNumber: fmt } = useUiStrings();
	if (value === undefined || max === undefined) return <span className="skeleton block h-[2.25rem] w-full rounded-[0.5rem]" />;
	const text = t("overall.ofTotal", { n: fmt(value), total: fmt(max) });
	return (
		<div>
			<p className="sd-meter-l">
				<span>{label}</span>
				<b>
					{text}
					{max > 0 && <span className="ml-[0.375rem] font-semibold text-gms-ink-3">{Math.round((value / max) * 100)}%</span>}
				</b>
			</p>
			<Meter value={value} max={max} label={`${label}: ${text}`} />
		</div>
	);
}
